import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ProfessionalCard } from "@/components/shared/Common";
import { toast } from "sonner";
import { COMPANY } from "@/constants/testIds";
import { ROLE_CATEGORIES, KIT_ITEMS } from "@/pages/RegisterStaff";
import { Search, SlidersHorizontal, X } from "lucide-react";

const RANKING_CATEGORIES = [
  { key: "Security Guard", label: "Security Guards" },
  { key: "CPO / VPO", label: "CPOs / VPOs" },
  { key: "Cluster Team", label: "Cluster Teams" },
];

export default function CompanySearch() {
  const [sp, setSp] = useSearchParams();
  const [q, setQ] = useState({
    county: sp.get("county") || "",
    role: sp.get("role") || "Security Guard",
    min_experience: sp.get("min_experience") || "",
    job_county: sp.get("job_county") || "",
    country: sp.get("country") || "",                                       // "" | "IE" | "UK"
    licence: sp.get("licence") || "",                                        // "" | "PSA" | "SIA"
    qualification: sp.get("qualification") || "",                            // "" | "cp" | "first_aid"
    willing_to_travel: sp.get("willing_to_travel") === "true",
    willing_cross_border: sp.get("willing_cross_border") === "true",
    required_kit: sp.get("required_kit") ? sp.get("required_kit").split(",") : [],
    required_qualifications: sp.get("required_qualifications") ? sp.get("required_qualifications").split(",") : [],
  });
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const toggleKit = (item) => {
    setQ((cur) => {
      const s = new Set(cur.required_kit);
      if (s.has(item)) s.delete(item); else s.add(item);
      return { ...cur, required_kit: Array.from(s) };
    });
  };

  const search = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (q.county) params.set("county", q.county);
      if (q.role && q.role !== "Cluster Team") params.set("role", q.role);
      if (q.min_experience) params.set("min_experience", q.min_experience);
      if (q.job_county) params.set("job_county", q.job_county);
      if (q.country) params.set("country", q.country);
      if (q.licence) params.set("licence", q.licence);
      if (q.qualification) params.set("qualification", q.qualification);
      if (q.willing_to_travel) params.set("willing_to_travel", "true");
      if (q.willing_cross_border) params.set("willing_cross_border", "true");
      if (q.required_kit.length) params.set("required_kit", q.required_kit.join(","));
      if (q.required_qualifications.length) params.set("required_qualifications", q.required_qualifications.join(","));

      // Sync URL so profile detail page can inherit the same score context
      setSp(params, { replace: true });

      if (q.role === "Cluster Team") {
        const { data } = await api.get(`/public/teams?${params.toString()}`);
        setResults(data.map((t) => ({ __team: true, ...t })));
      } else {
        const { data } = await api.get(`/public/professionals?${params.toString()}`);
        setResults(data);
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  useEffect(() => { search();  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-outfit font-bold text-white">Search Security Workforce</h1>
        <p className="text-white/60 mt-1 font-manrope text-sm">Ranking categories are kept separate — Security Guards, CPOs/VPOs and Cluster Teams are never mixed.</p>
      </div>

      {/* Ranking category tabs */}
      <div className="flex flex-wrap gap-2" data-testid="ranking-tabs">
        {RANKING_CATEGORIES.map((r) => (
          <button
            key={r.key}
            onClick={() => setQ({ ...q, role: r.key })}
            className={`px-4 py-2 rounded-md font-outfit font-semibold text-sm border transition ${
              q.role === r.key ? "bg-navy-900 text-white border-navy-900" : "bg-[#0A0A0A] text-white border-white/10 hover:border-brand"
            }`}
            data-testid={`rank-${r.key.replace(/\W+/g,"-")}`}
          >
            {r.label}
          </button>
        ))}
      </div>

      {/* Country / licence / travel filters */}
      <div className="card-flat p-5 grid md:grid-cols-5 gap-3 items-end">
        <div>
          <Label>Country</Label>
          <Select value={q.country || "any"} onValueChange={(v) => setQ({ ...q, country: v === "any" ? "" : v })}>
            <SelectTrigger data-testid="search-country"><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any country</SelectItem>
              <SelectItem value="IE">Ireland</SelectItem>
              <SelectItem value="UK">United Kingdom</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Licence status</Label>
          <Select value={q.licence || "any"} onValueChange={(v) => setQ({ ...q, licence: v === "any" ? "" : v })}>
            <SelectTrigger data-testid="search-licence"><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any licence</SelectItem>
              <SelectItem value="PSA">PSA (Ireland)</SelectItem>
              <SelectItem value="SIA">SIA (UK)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Qualification</Label>
          <Select value={q.qualification || "any"} onValueChange={(v) => setQ({ ...q, qualification: v === "any" ? "" : v })}>
            <SelectTrigger data-testid="search-qual"><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any qualification</SelectItem>
              <SelectItem value="cp">Close Protection</SelectItem>
              <SelectItem value="first_aid">First Aid</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <label className="flex items-center gap-2 text-sm font-manrope h-11">
          <Checkbox checked={q.willing_to_travel} onCheckedChange={(v) => setQ({ ...q, willing_to_travel: !!v })} data-testid="search-travel" /> Willing to travel
        </label>
        <label className="flex items-center gap-2 text-sm font-manrope h-11">
          <Checkbox checked={q.willing_cross_border} onCheckedChange={(v) => setQ({ ...q, willing_cross_border: !!v })} data-testid="search-cross-border" /> Cross-border (IE ↔ UK)
        </label>
      </div>

      {/* Core filters */}
      <div className="card-flat p-5 grid md:grid-cols-5 gap-3 items-end">
        <div>
          <Label>Job county</Label>
          <Input value={q.job_county} onChange={(e) => setQ({ ...q, job_county: e.target.value })} placeholder="e.g., Dublin" data-testid="search-job-county" />
        </div>
        <div>
          <Label>Filter county</Label>
          <Input value={q.county} onChange={(e) => setQ({ ...q, county: e.target.value })} placeholder="Any" data-testid="search-county" />
        </div>
        {q.role !== "Cluster Team" && (
          <div>
            <Label>Role (fine-tune)</Label>
            <Select value={q.role} onValueChange={(v) => setQ({ ...q, role: v })}>
              <SelectTrigger data-testid={COMPANY.filterRole}><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLE_CATEGORIES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div>
          <Label>Min exp (yrs)</Label>
          <Input type="number" min={0} value={q.min_experience} onChange={(e) => setQ({ ...q, min_experience: e.target.value })} data-testid="search-min-exp" />
        </div>
        <div className="flex gap-2">
          <Button
            onClick={search}
            disabled={busy}
            className="bg-brand hover:bg-brand-hover text-white h-11 font-manrope font-semibold flex-1"
            data-testid={COMPANY.searchProfessionals}
          >
            <Search className="w-4 h-4 mr-2" /> Search
          </Button>
          <Button
            variant="outline"
            className="h-11 border-white/10"
            onClick={() => setShowAdvanced((v) => !v)}
            data-testid="search-advanced-toggle"
            title="Job requirements"
          >
            <SlidersHorizontal className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Advanced job spec (drives match score) */}
      {showAdvanced && (
        <div className="card-flat p-5 space-y-3" data-testid="search-advanced-panel">
          <div className="flex justify-between items-center">
            <div>
              <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold">Job Requirements</div>
              <div className="text-sm text-white/60 font-manrope">These drive the Job Match Score. Only tick what the job actually needs.</div>
            </div>
            <button onClick={() => setShowAdvanced(false)} className="text-white/50 hover:text-white"><X className="w-4 h-4" /></button>
          </div>
          <div>
            <Label className="mb-1 block">Required kit</Label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {KIT_ITEMS.map((k) => (
                <label key={k} className="flex items-center gap-2 text-xs font-manrope cursor-pointer">
                  <Checkbox checked={q.required_kit.includes(k)} onCheckedChange={() => toggleKit(k)} data-testid={`req-kit-${k.replace(/\W+/g,"-")}`} />
                  <span>{k}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Results */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {results.length === 0 && <div className="text-white/60 col-span-full text-center py-10 font-manrope">No results yet — try adjusting filters.</div>}
        {results.map((p) => {
          if (p.__team) return <TeamResultCard key={p.id} team={p} />;
          return <ProfessionalCard key={p.secusource_id} p={p} />;
        })}
      </div>
    </div>
  );
}

function TeamResultCard({ team }) {
  return (
    <div className="card-flat p-5 flex flex-col gap-3 reveal" data-testid={`team-result-${team.id}`}>
      <div className="flex items-center justify-between">
        <span className="ss-id">{team.lead_secusource_id}</span>
        <span className="chip">{team.num_members} members</span>
      </div>
      <div className="font-outfit font-semibold text-white">{team.name}</div>
      <div className="text-xs text-white/60 font-manrope">
        Combined exp: {team.combined_experience}yr · {team.combined_kit?.length || 0} kit items
      </div>
      {team.match && (
        <div className="rounded-md border border-white/10 p-2 bg-[#0E0E0E]">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-widest text-white/60 font-outfit font-bold">Team Match Score</div>
            <div className="font-outfit font-bold text-white">{Math.round(team.match.score)}<span className="text-xs text-white/60">%</span></div>
          </div>
          <div className="mt-1 text-xs text-white/70 font-manrope">{team.match.label}</div>
        </div>
      )}
    </div>
  );
}
